/* eslint-disable react/no-unused-prop-types */
import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import { isEmpty } from 'lodash/fp';
import { useTranslation } from 'gatsby-plugin-react-i18next';

import AutomatedSuggestedContent from '#contentful/AutomatedSuggestedContent';
import ChipLink from '#components/ChipLink';
import type { Category } from '#contentful/ContentfulCard';
import type { GatsbyImageProps } from '#contentful/Image';
import PageHeading from '#components/PageHeading';
import RichText from '#components/RichText';
import onScrollAnimate from '#utils/onScrollAnimate';

import PersonCard from '#components/PersonCard';
import type { AnySectionDefinition } from '#components/Section';
import Section from '#components/Section';
import useGatePage from '#hooks/useGatePage';
import columnCountOptions from '#utils/columnCountOptions';
import type { BlogCardDefinition } from '#contentful/ContentfulCard/BlogCard';
import clsx from 'clsx';
import { Text } from '@/components/Text';
import generateAutomatedContent from './_generateAutomatedContent';
import BlogComponent from './BlogComponent';

interface Author {
  id: string,
  name: string,
  title: string | JSX.Element,
  image?: GatsbyImageProps,
  pageTemplate: {
    composePage: {
      slug: string,
    }[],
  }[],
}

const translationKey = 'Pages.BlogPage';

const BlogPage = ({ data }: any) => {
  const { t } = useTranslation();

  useEffect(() => {
    onScrollAnimate.register([
      { className: 'reveal' },
      { className: 'flair', distanceToReveal: 200 },
    ]);
  }, []);

  const {
    title = '',
    subtitle = '',
    minuteRead = 0,
    datePublished = '',
    headingBackground = null,
    authors = [],
    blogContent = [],
    bottomContent = null,
    categories = [],
    newsroomCategories = [],
    introParagraphPageTemplateBlogPost = null,
    introParagraphListColumnCount = 1,
    gateContent,
    moreArticles,
  } = data?.page?.content ?? {};
  const { slug } = data?.page ?? {};
  const gateContentSlug = gateContent?.slug;

  useGatePage(slug, gateContentSlug);

  const categoryName = categories?.[0]?.displayName;
  const hasMultipleAuthors = authors?.length > 1;

  const automatedContent = generateAutomatedContent(categoryName?.toLowerCase());

  const prioritizedSuggestedContentIds = moreArticles?.map(
    ({ blogPage }: BlogCardDefinition) => blogPage?.content?.id,
  );

  const introParagraphOptions = columnCountOptions(introParagraphListColumnCount ?? 1);
  return (
    <div className="container">
      <div className="flex flex-wrap justify-center">
        <div className="w-full">
          <PageHeading
            {...{
              title,
              subtitle,
              minuteRead,
              datePublished,
              authors,
            }}
            background={headingBackground}
            extendContentWidth
            overline={categoryName}
            addOverlay
          />
        </div>

        <div className="w-10/12 md:w-8/12 lg:w-10/12 mt-14 flex flex-wrap justify-center items-center">
          {!isEmpty(blogContent) && blogContent.map((component: any, index:number) => (
            <div key={component.id} className="w-full px-8">
              {index === 0 && authors && (
                <div
                  className={clsx(
                    'w-full lg:w-1/2 lg:float-right flex flex-wrap gap-y-10',
                    hasMultipleAuthors ? 'justify-between' : 'justify-center',
                  )}
                >
                  {!isEmpty(authors) && authors.map(({
                    id,
                    name,
                    title: jobTitle,
                    image,
                    pageTemplate,
                  }: Author) => (
                    <div
                      key={id}
                      className="pb-16 w-full"
                    >
                      <PersonCard
                        photo={image}
                        name={name}
                        jobTitle={jobTitle}
                        profilePage={pageTemplate}
                      />
                    </div>
                  ))}
                </div>
              )}
              {index === 0 && (
                <>
                  <Text variant="body1" className="text-impblue500">
                    {datePublished}
                    {' '}
                    |
                    {' '}
                    <span className="font-bold">{`${minuteRead} ${t(`${translationKey}.MinuteRead`)}`}</span>
                  </Text>
                  <RichText
                    content={introParagraphPageTemplateBlogPost}
                    options={introParagraphOptions}
                  />
                </>
              )}
              <div className="blog-post-content basic-prose">
                <BlogComponent content={component} />
              </div>
            </div>
          ))}

          {categories?.length && (
            <div className="w-full px-8 flex flex-wrap gap-x-4">
              {!newsroomCategories && categories?.map(
                ({ id, displayName, listingPage }: Category) => (
                  <div key={id} className="typography body2">
                    <ChipLink
                      label={displayName}
                      href={`/${listingPage?.[0]?.composePage?.[0]?.slug ?? '/thoughts'}`}
                    />
                  </div>
                ),
              )}
              {newsroomCategories && newsroomCategories?.map(
                ({ id, displayName, listingPage }: Category) => (
                  <div key={id} className="typography body2">
                    <ChipLink
                      label={displayName}
                      href={`/${listingPage?.[0]?.composePage?.[0]?.slug ?? '/newsroom'}`}
                    />
                  </div>
                ),
              )}
            </div>
          )}

          {bottomContent && bottomContent.map((section: AnySectionDefinition) => (
            <div key={section.id} className="w-full flex flex-wrap">
              <Section section={section} />
            </div>
          ))}
        </div>
        <div className="w-full mt-20 flex flex-wrap justify-center">
          {automatedContent && (
            <div
              className="w-full lg:w-10/12"
              data-testid="automated-suggested-content"
            >
              <AutomatedSuggestedContent
                content={automatedContent}
                prioritizedContent={prioritizedSuggestedContentIds}
              />
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default BlogPage;

export { Head } from '#components/Head';

export const pageQuery = graphql`
  query blog($slug: String!, $locale: String!) {
    page: contentfulComposePage(
      slug: {eq: $slug},
      node_locale: {eq: $locale}
    ) {
      content {
        ...BlogPageTemplate
      }
      seo {
        ...SEO
        lang: node_locale
      }
      slug
    }
    locales: allLocale(filter: {language: {eq: $locale}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
